import React, { useState, useEffect, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    Box,
    VStack,
    useToast,
    Center,
    Checkbox
} from '@chakra-ui/react';
import Loader from 'components/loader/Loader';

// Lazy load ReactMarkdown for better initial load performance
const ReactMarkdown = lazy(() => import('react-markdown'));

// Memoized Loading Component with new Loader
const LoadingModal = memo(() => (
    <Modal isOpen={true} onClose={() => { }} closeOnOverlayClick={false} closeOnEsc={false} size="xl">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent>
            {/* <ModalBody py={10}> */}
            {/* <Loader
                    fullScreen={false}
                    message="Loading Announcement..."
                    size={150}
                    messageColor="gray.700"
                    showMessage={true}
                /> */}
            {/* </ModalBody> */}
        </ModalContent>
    </Modal>
));

LoadingModal.displayName = 'LoadingModal';

// Memoized Markdown Content Component
const MarkdownContent = memo(({ content }) => {
    const markdownComponents = useMemo(() => ({
        h1: ({ node, ...props }) => <Text as="h1" fontSize="2xl" fontWeight="bold" mb={4} mt={6} color="gray.800" {...props} />,
        h2: ({ node, ...props }) => <Text as="h2" fontSize="xl" fontWeight="bold" mb={3} mt={5} color="gray.700" {...props} />,
        h3: ({ node, ...props }) => <Text as="h3" fontSize="lg" fontWeight="semibold" mb={2} mt={4} color="gray.700" {...props} />,
        h4: ({ node, ...props }) => <Text as="h4" fontSize="md" fontWeight="semibold" mb={2} mt={3} color="gray.700" {...props} />,
        p: ({ node, ...props }) => <Text mb={4} lineHeight="1.8" color="gray.600" {...props} />,
        ul: ({ node, ...props }) => <Box as="ul" pl={6} mb={4} {...props} />,
        ol: ({ node, ...props }) => <Box as="ol" pl={6} mb={4} {...props} />,
        li: ({ node, ...props }) => <Text as="li" mb={2} lineHeight="1.7" color="gray.600" {...props} />,
        strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color="gray.800" {...props} />,
        em: ({ node, ...props }) => <Text as="em" fontStyle="italic" {...props} />,
        blockquote: ({ node, ...props }) => (
            <Box
                as="blockquote"
                borderLeft="4px"
                borderColor="brand.400"
                pl={4}
                py={2}
                my={4}
                bg="brand.100"
                fontStyle="italic"
                color="gray.700"
                {...props}
            />
        ),
        code: ({ node, inline, ...props }) =>
            inline ? (
                <Text as="code" bg="gray.100" px={2} py={1} borderRadius="md" fontSize="sm" {...props} />
            ) : (
                <Box as="code" display="block" bg="gray.900" color="green.300" p={4} borderRadius="md" my={4} overflowX="auto" {...props} />
            ),
        hr: ({ node, ...props }) => <Box as="hr" my={6} borderColor="gray.300" {...props} />,
    }), []);

    const scrollbarStyles = useMemo(() => ({
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
        },
    }), []);

    return (
        <Box
            bg="white"
            p={8}
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
            maxH="50vh"
            overflowY="auto"
            css={scrollbarStyles}
        >
            <Suspense fallback={
                <Center py={8}>
                    <Loader
                        fullScreen={false}
                        message="Loading content..."
                        size={80}
                        messageColor="gray.600"
                        showMessage={false}
                    />
                </Center>
            }>
                <ReactMarkdown components={markdownComponents}>
                    {content}
                </ReactMarkdown>
            </Suspense>
        </Box>
    );
});

MarkdownContent.displayName = 'MarkdownContent';

// Memoized Modal Header Component
const TermsModalHeader = memo(({ version, effectiveDate }) => {
    const formattedDate = useMemo(() =>
        new Date(effectiveDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }), [effectiveDate]
    );

    return (
        <ModalHeader
            borderBottom="2px"
            borderColor="brand.500"
            bg="gray.50"
            borderTopRadius="xl"
            py={6}
        >
            <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                    Important Announcement
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                    Version {version} • Effective {formattedDate}
                </Text>
            </VStack>
        </ModalHeader>
    );
});

TermsModalHeader.displayName = 'TermsModalHeader';

const TermsModal = ({ isOpen, onAccept, apiCall, termsData = null, isAlreadyAccepted = false }) => {
    const [terms, setTerms] = useState(termsData); // Initialize with passed data
    const [loading, setLoading] = useState(!termsData); // Skip loading if data provided
    const [accepting, setAccepting] = useState(false);
    const [hasReadTerms, setHasReadTerms] = useState(isAlreadyAccepted); // Auto-check if accepted
    const toast = useToast();

    const fetchTerms = useCallback(async () => {
        // Skip fetch if we already have terms data
        if (termsData) {
            setTerms(termsData);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await apiCall('/api/terms/current');

            if (data.hasTerms && data.needsAcceptance) {
                setTerms(data.terms);
            } else {
                setTerms(null);
            }
        } catch (error) {
            console.error('Error fetching announcement:', error);
            toast({
                title: 'Error loading announcement',
                description: 'Unable to load announcement',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setTerms(null);
        } finally {
            setLoading(false);
        }
    }, [apiCall, toast, termsData]);

    const handleAccept = useCallback(async () => {
        if (!hasReadTerms) {
            toast({
                title: 'Please confirm',
                description: 'Please confirm that you have read the announcement',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setAccepting(true);
            await apiCall('/api/terms/accept', {
                method: 'POST',
                body: JSON.stringify({ version: terms.version })
            });

            toast({
                title: 'Acknowledged',
                description: 'You have successfully acknowledged the announcement',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            onAccept();
        } catch (error) {
            console.error('Error accepting announcement:', error);
            toast({
                title: 'Error',
                description: 'Failed to acknowledge announcement. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setAccepting(false);
        }
    }, [hasReadTerms, apiCall, terms, toast, onAccept]);

    const handleCheckboxChange = useCallback((e) => {
        setHasReadTerms(e.target.checked);
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchTerms();
        }
    }, [isOpen, fetchTerms]);

    // Early return if modal shouldn't be shown
    if (!isOpen || (!loading && !terms)) {
        return null;
    }

    if (loading) {
        return <LoadingModal />;
    }

    return (
        <Modal
            isOpen={true}
            onClose={() => { }}
            closeOnOverlayClick={false}
            closeOnEsc={false}
            size="3xl"
        >
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
            <ModalContent maxH="95vh" borderRadius="xl" boxShadow="2xl">
                <TermsModalHeader
                    version={terms.version}
                    effectiveDate={terms.effectiveDate}
                />

                <ModalBody py={6} overflowY="auto">
                    <MarkdownContent content={terms.content} />
                    {!isAlreadyAccepted && (
                        <Box mt={6} bg="brand.100" p={4} borderRadius="md" border="1px" borderColor="brand.200">
                            <Checkbox
                                isChecked={hasReadTerms}
                                onChange={handleCheckboxChange}
                                colorScheme="brand"
                                size="lg"
                            >
                                <Text fontSize="md" fontWeight="medium" color="gray.700">
                                    I have read and acknowledge this announcement
                                </Text>
                            </Checkbox>
                        </Box>
                    )}
                </ModalBody>

                <ModalFooter borderTop="2px" borderColor="gray.200" bg="gray.50" borderBottomRadius="xl" py={6}>
                    <VStack w="100%" spacing={3}>
                        <Button
                            colorScheme="brand"
                            size="lg"
                            w="100%"
                            onClick={isAlreadyAccepted ? onAccept : handleAccept} // Close immediately if accepted
                            isLoading={accepting}
                            isDisabled={!isAlreadyAccepted && !hasReadTerms} // Disable only if not accepted
                            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                            transition="all 0.2s"
                        >
                            {isAlreadyAccepted ? 'Close' : 'Acknowledge and Continue'}
                        </Button>
                        <Text fontSize="xs" color="gray.500" textAlign="center" px={4}>
                            {isAlreadyAccepted
                                ? 'You have already acknowledged this announcement.'
                                : 'You must acknowledge this announcement to use the Employee Workspace.'
                            }
                        </Text>
                    </VStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default memo(TermsModal);